import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Tooltip,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { IKReactWindowedSelect } from '../../../../../ui-kit'
import type { CmFormValues } from '../../../../interfaces/FileDetailsResponse'
import { TypeBadge } from '../../../../../utils/mediaLibrary'
import type { CMFieldProps } from '.'
import EllipsisMiddle from '../../../EllipsisMiddle'

const CMMultiSelectField = ({
  cmField,
  disabled,
  isUpload,
}: CMFieldProps): ReactElement => {
  const { t } = useTranslation()

  return (
    <Field name={`['${cmField.name}']`} key={`['${cmField.name}']`}>
      {({ field, form }: FieldProps<string | string[], CmFormValues>) => {
        let formattedValue: Array<{
          label: JSX.Element | string
          value: string | boolean | number
        }> = []

        if (Array.isArray(field.value)) {
          formattedValue = field.value.map(item =>
            typeof item !== 'object'
              ? {
                  label: disabled || cmField.schema.readOnly ? (
                    String(item)
                  ) : (
                    <>
                      {String(item)} <TypeBadge val={item} />
                    </>
                  ),
                  value: item,
                }
              : item,
          )
        }

        const cmFieldSelectOptions = cmField.schema.selectOptions ?? []

        return (
          <FormControl
            marginBottom={isUpload ? 0 : '30px'}
            isInvalid={Boolean(form.errors[cmField.name])}
            isRequired={cmField.schema.isValueRequired}
            isDisabled={disabled || cmField.schema.readOnly}
          >
            <Tooltip label={cmField.label} placement="top-start">
              <FormLabel requiredIndicator={<></>}>
                <Flex maxWidth="20vw">
                  <EllipsisMiddle
                    text={cmField.label}
                    required={cmField.schema.isValueRequired}
                    className="centerText"
                  />
                </Flex>
              </FormLabel>
            </Tooltip>
            <Tooltip
              isDisabled={!cmField.schema.readOnly}
              label={t('read_only', 'Read only')}
              shouldWrapChildren
            >
              <IKReactWindowedSelect
                {...field}
                placeholder={
                  Array.isArray(cmField?.schema?.defaultValue) &&
                  cmField?.schema?.defaultValue.length > 0 &&
                  isUpload
                    ? `${cmField.schema.defaultValue
                        .map(val => `${val}[${typeof val}]`)
                        .join(', ')} (Default value)`
                    : ''
                }
                variant="compact"
                captureMenuScroll={false}
                menuPlacement="auto"
                isClearable
                isMulti
                isDisabled={disabled || cmField.schema.readOnly}
                value={formattedValue}
                options={cmFieldSelectOptions?.map(item => ({
                  label: (
                    <>
                      {String(item)} <TypeBadge val={item} />
                    </>
                  ),
                  value: item,
                }))}
                onChange={newVals => {
                  form.setTouched({ ...form.touched, [field.name]: true })
                  if (newVals.length === 0) {
                    form.setFieldValue(field.name, null)
                  } else {
                    form.setFieldValue(
                      field.name,
                      newVals?.map(newVal => newVal.value),
                    )
                  }
                }}
              />
            </Tooltip>
            <FormErrorMessage>{form.errors[cmField.name]}</FormErrorMessage>
          </FormControl>
        )
      }}
    </Field>
  )
}

export default CMMultiSelectField
